import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { AlertTriangle, LoaderCircle, Plus, Search, Trash2, Unlink, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import ImportErrorsTable from '../components/ImportErrorsTable'
import ItemPicker from '../components/ItemPicker'
import MoneyInput from '../components/MoneyInput'
import { formatMoney } from '../lib/money'
import {
  actingKeys, assignNode, draftCalc, draftDiscount, draftFromImport, draftFromPriceItems, newDraftItem, priceItemsProblem, priceSnapshot, switchDiscountMode,
  toDiscountInput, toPriceItemInputs, visibleItems, type DiscountMode, type DraftDiscount, type DraftPriceItem,
} from '../lib/priceItems'
import type { CostTree } from '../lib/tree'
import type { CostContractDiscountInput, CostContractPriceItem, CostContractPriceItemInput, CostImportError, CostPriceImportItem } from '../types'
import PriceFileImport from './PriceFileImport'
import PriceItemsTable from './PriceItemsTable'

interface Props {
  open: boolean
  contractId: string
  projectId: string
  items: CostContractPriceItem[]
  discountPercent: string | null
  discountAmount: string | null
  tree: CostTree
  /** The contract has a manual allocation that saving the breakdown replaces. */
  replacesLines?: boolean
  currency: string
  locale: string
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the breakdown; the dialog stays open on failure. */
  onSave: (items: CostContractPriceItemInput[], discount: CostContractDiscountInput | null) => Promise<boolean>
}

const EMPTY_DISCOUNT: DraftDiscount = { mode: 'percent', value: null, valid: true }
const NO_EXCLUDE: string[] = []

/** "Upravit rozpis ceny" (2026-10-06 spec §8): edit, add and remove items, map them (one by one or in bulk), set the discounts. */
const PriceItemsDialog: React.FC<Props> = ({ open, contractId, projectId, items, discountPercent, discountAmount, tree, replacesLines = false, currency, locale, onOpenChange, onSave }) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<DraftPriceItem[]>([])
  const [discount, setDiscount] = useState<DraftDiscount>(EMPTY_DISCOUNT)
  const [initial, setInitial] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [unmappedOnly, setUnmappedOnly] = useState(false)
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [importErrors, setImportErrors] = useState<CostImportError[]>([])

  useEffect(() => {
    if (!open) return
    const d = draftFromPriceItems(items)
    const disc = draftDiscount({ discountPercent, discountAmount })
    setDraft(d)
    setDiscount(disc)
    setInitial(priceSnapshot(d, disc))
    setSelected(new Set())
    setUnmappedOnly(false)
    setQuery('')
    setSaving(false)
    setDiscarding(false)
    setImportErrors([])
    // Only on opening: a background reload must not wipe the edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const calc = useMemo(() => draftCalc(draft, discount), [draft, discount])
  const finals = useMemo(() => new Map(draft.map((d, i) => [d.key, calc.finals[i]] as [string, string])), [draft, calc])
  const visible = useMemo(() => visibleItems(draft, { unmappedOnly, query }), [draft, unmappedOnly, query])
  const acting = useMemo(() => actingKeys(visible, selected), [visible, selected])
  const problem = priceItemsProblem(draft, discount, calc)
  const dirty = priceSnapshot(draft, discount) !== initial
  const money = (v: string) => formatMoney(v, { locale, currency })
  const allVisibleSelected = visible.length > 0 && visible.every((i) => selected.has(i.key))

  const change = (key: string, patch: Partial<DraftPriceItem>) => setDraft((d) => d.map((i) => (i.key === key ? { ...i, ...patch } : i)))
  const toggle = (key: string) => setSelected((s) => {
    const next = new Set(s)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map((i) => i.key)))
  const assign = (nodeId: string | null) => {
    setDraft((d) => assignNode(d, acting, nodeId))
    setSelected(new Set())
  }
  const removeSelected = () => {
    setDraft((d) => d.filter((i) => !acting.has(i.key)))
    setSelected(new Set())
  }
  const addItem = () => {
    setQuery('')
    setUnmappedOnly(false)
    setDraft((d) => [...d, newDraftItem(d.length ? d[d.length - 1].section : '')])
  }
  const addImported = (rows: CostPriceImportItem[]) => {
    setQuery('')
    setUnmappedOnly(false)
    setImportErrors([])
    setDraft((d) => [...d, ...draftFromImport(rows)])
    toastr.success('', t('CostControl_price_file_added', { count: rows.length }))
  }
  const close = () => {
    if (saving) return
    if (dirty) setDiscarding(true)
    else onOpenChange(false)
  }
  const save = async () => {
    if (problem || !dirty || saving) return
    setSaving(true)
    const ok = await onSave(toPriceItemInputs(draft), toDiscountInput(discount))
    setSaving(false)
    if (ok) onOpenChange(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
        <DialogContent className="flex max-h-[92vh] flex-col sm:max-w-[min(96vw,88rem)]">
          <DialogHeader>
            <DialogTitle>{t('CostControl_price_dialog_title')}</DialogTitle>
            <DialogDescription>{t('CostControl_price_dialog_hint')}</DialogDescription>
            {replacesLines && <p className="text-sm text-muted-foreground">{t('CostControl_price_dialog_replaces_lines')}</p>}
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="h-8" onClick={addItem} disabled={saving}>
              <Plus className="h-4 w-4" />
              {t('CostControl_price_add_item')}
            </Button>
            <PriceFileImport contractId={contractId} projectId={projectId} disabled={saving} onRows={addImported} onErrors={setImportErrors} />
            {acting.size > 0 && (
              <>
                <span className="text-sm text-muted-foreground">{t('CostControl_price_selected', { count: acting.size })}</span>
                <ItemPicker tree={tree} excludeIds={NO_EXCLUDE} disabled={saving} label={t('CostControl_price_assign')} onPick={(nodeId) => assign(nodeId)} />
                <Button variant="ghost" size="sm" className="h-8" onClick={() => assign(null)} disabled={saving}>
                  <Unlink className="h-4 w-4" />
                  {t('CostControl_price_unassign')}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 text-destructive hover:text-destructive" onClick={removeSelected} disabled={saving}>
                  <Trash2 className="h-4 w-4" />
                  {t('CostControl_price_remove_selected')}
                </Button>
              </>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={unmappedOnly} onCheckedChange={(v) => { setUnmappedOnly(v); setSelected(new Set()) }} />
                {t('CostControl_price_unmapped_only')}
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={query} onChange={(e) => { setQuery(e.target.value); setSelected(new Set()) }} placeholder={t('CostControl_price_search')} className="h-8 w-[200px] pl-8 text-sm" />
              </div>
            </div>
          </div>
          {importErrors.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex-1">{t('CostControl_price_file_has_errors')}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setImportErrors([])} aria-label={t('Common_close')}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <ImportErrorsTable errors={importErrors} />
            </div>
          )}
          <PriceItemsTable
            items={visible}
            hasDraft={draft.length > 0}
            finals={finals}
            tree={tree}
            selected={selected}
            allSelected={allVisibleSelected}
            disabled={saving}
            currency={currency}
            locale={locale}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onChange={change}
          />
          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 text-sm">
            <span>{t('CostControl_price_gross')}: <strong className="tabular-nums">{money(calc.gross)}</strong></span>
            <span>{t('CostControl_price_after_item_discounts')}: <strong className="tabular-nums">{money(calc.afterItemDiscounts)}</strong></span>
            <div className="flex items-center gap-2">
              <span>{t('CostControl_price_contract_discount')}</span>
              <Select value={discount.mode} onValueChange={(mode) => setDiscount((d) => switchDiscountMode(d, mode as DiscountMode))} disabled={saving}>
                <SelectTrigger className="h-8 w-20" aria-label={t('CostControl_price_discount_mode')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">%</SelectItem>
                  <SelectItem value="amount">{currency}</SelectItem>
                </SelectContent>
              </Select>
              <MoneyInput value={discount.value} onChange={(value, valid) => setDiscount((d) => ({ ...d, value, valid }))} disabled={saving} className="h-8 w-32" aria-label={t('CostControl_price_contract_discount')} />
              {discount.mode === 'percent' && calc.contractDiscount !== '0.00' && <span className="tabular-nums text-muted-foreground">−{money(calc.contractDiscount)}</span>}
            </div>
            <span>{t('CostControl_price_total')}: <strong className="tabular-nums">{money(calc.total)}</strong></span>
            {calc.unmappedCount > 0 && <Badge variant="warning">{t('CostControl_price_unmapped_count', { count: calc.unmappedCount })}</Badge>}
          </div>
          {problem && <p className="text-sm text-destructive">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={close} disabled={saving}>{t('Common_cancel')}</Button>
            <Button onClick={save} disabled={saving || !!problem || !dirty}>
              {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={discarding}
        title={t('CostControl_price_discard_title')}
        description={t('CostControl_price_discard_text')}
        confirmLabel={t('CostControl_price_discard')}
        variant="destructive"
        onCancel={() => setDiscarding(false)}
        onConfirm={() => { setDiscarding(false); onOpenChange(false) }}
      />
    </>
  )
}
export default PriceItemsDialog
